import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';

import { ClientGpDto, ContactRequestDto } from 'src/api/FiretrolApiClient';
import {
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
} from 'src/api/queries/organization/useContactMutations';
import {
  FIELD_LIMITS,
  getErrorMessage,
  showSuccessToast,
  toFormikFieldErrors,
} from 'src/utils';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';
import * as Yup from 'yup';
import { ClientSelector } from 'src/app/selectors/ClientSelector';

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required()
    .min(1, 'First Name Required')
    .max(
      FIELD_LIMITS.singleLine,
      `First Name must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('First Name'),
  lastName: Yup.string()
    .required()
    .min(1, 'Last Name Required')
    .max(
      FIELD_LIMITS.singleLine,
      `Last Name must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Last Name'),
  phone: Yup.string()
    .max(
      FIELD_LIMITS.singleLine,
      `Phone Number must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Phone Number'),
  email: Yup.string()
    .required()
    .email('Enter a valid email address')
    .max(
      FIELD_LIMITS.singleLine,
      `Email must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Email'),
  customerName: Yup.string().nullable().label('Customer Name'),
  customerNumber: Yup.string()
    .required('Customer is required')
    .label('Customer'),
});

const initialValues: Pick<
  ContactRequestDto,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'email'
  | 'customerName'
  | 'customerNumber'
> = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  customerName: '',
  customerNumber: '',
};

interface ICreateRoleForm {
  selectedContact: ContactRequestDto | null;
  setCreateContactDialogOpen: (
    isOpen: boolean,
    contact?: ContactRequestDto,
  ) => void;
  clientOptions?: ClientGpDto[];
}

export const CreateContactForm: React.FC<ICreateRoleForm> = ({
  selectedContact,
  setCreateContactDialogOpen,
  clientOptions,
}) => {
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const [error, setError] = React.useState<string | null>(null);

  const closeDialog = () => {
    setCreateContactDialogOpen(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: selectedContact ? selectedContact : initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      setError(null);
      try {
        if (selectedContact) {
          await updateContact.mutateAsync({
            id: values.id!,
            request: values,
          });
          setCreateContactDialogOpen(false);
          showSuccessToast('Contact successfully updated!');
        } else {
          await createContact.mutateAsync(values);
          setCreateContactDialogOpen(false);
          showSuccessToast('Contact successfully created!');
        }
      } catch (err: unknown) {
        for (const [field, message] of Object.entries(
          toFormikFieldErrors(err),
        )) {
          helpers.setFieldError(field, message);
        }
        setError(getErrorMessage(err));
      }
    },
  });

  const fieldError = (
    field: 'firstName' | 'lastName' | 'phone' | 'email' | 'customerNumber',
  ): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  const removeContact = async () => {
    if (!selectedContact?.id) return;
    await deleteContact.mutateAsync(selectedContact.id);
    setCreateContactDialogOpen(false);
  };

  return (
    <>
      <Box>
        <Stack>
          <Input.Wrapper
            label="Customer"
            required
            error={fieldError('customerNumber')}
          >
            <ClientSelector
              onChange={(client) => {
                formik.setFieldValue('customerName', client?.custName ?? '');
                formik.setFieldValue(
                  'customerNumber',
                  client?.custNumber ?? '',
                );
              }}
              initialValue={
                formik.values.customerNumber
                  ? {
                      custNumber: formik.values.customerNumber,
                      custName: formik.values.customerName,
                    }
                  : undefined
              }
              clientOptions={clientOptions}
            />
          </Input.Wrapper>

          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="First Name"
                placeholder="First name..."
                required
                value={formik.values.firstName}
                onChange={(e) =>
                  formik.setFieldValue('firstName', e.target.value)
                }
                onBlur={formik.handleBlur}
                name="firstName"
                maxLength={FIELD_LIMITS.singleLine}
                error={fieldError('firstName')}
                onKeyDown={handleKeyDown}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Last Name"
                placeholder="Last name..."
                required
                value={formik.values.lastName}
                onChange={(e) =>
                  formik.setFieldValue('lastName', e.target.value)
                }
                onBlur={formik.handleBlur}
                name="lastName"
                maxLength={FIELD_LIMITS.singleLine}
                error={fieldError('lastName')}
                onKeyDown={handleKeyDown}
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label="Phone Number"
            placeholder="Phone number..."
            value={formik.values.phone}
            onChange={(e) => formik.setFieldValue('phone', e.target.value)}
            onBlur={formik.handleBlur}
            name="phone"
            maxLength={FIELD_LIMITS.singleLine}
            error={fieldError('phone')}
            onKeyDown={handleKeyDown}
          />

          <TextInput
            label="Email"
            placeholder="Email..."
            required
            value={formik.values.email}
            onChange={(e) => formik.setFieldValue('email', e.target.value)}
            onBlur={formik.handleBlur}
            name="email"
            maxLength={FIELD_LIMITS.singleLine}
            error={fieldError('email')}
            onKeyDown={handleKeyDown}
          />

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      <Group justify="space-between" mt="md">
        {selectedContact && (
          <InlineConfirmButton
            color="red"
            confirmButtonProps={{
              children: 'Confirm Delete',
            }}
            onConfirmClick={() => removeContact()}
          >
            Delete Contact
          </InlineConfirmButton>
        )}
        <Group>
          <ModalButton onClick={closeDialog} color="black" variant="light">
            Cancel
          </ModalButton>
          <ModalButton
            onClick={() => formik.handleSubmit()}
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
          >
            {selectedContact ? 'Save Changes' : 'Create Contact'}
          </ModalButton>
        </Group>
      </Group>
    </>
  );
};
